import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll-reveal driver.
 *
 * Deliberately conservative:
 *  - Elements are visible by default. The `js-reveal` class is only added to
 *    <html> once we know the observer is available, so a JS failure, an old
 *    browser or a crawler sees fully rendered content rather than a blank page.
 *  - Reveals fire once and then unobserve. Content never animates out or
 *    re-animates on scroll-up, which is distracting and slows reading.
 *  - Anything already in view on load is revealed immediately, so the first
 *    paint is never a page of invisible content.
 *  - Skipped entirely when the user asks for reduced motion.
 */
const useReveal = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    /* matchMedia is absent in jsdom and in some older/embedded browsers.
       Treat "cannot ask" as "assume reduced motion" — the page stays fully
       visible, which is the safe failure direction. */
    const reducedMotion =
      typeof window.matchMedia !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      root.classList.remove('js-reveal');
      return undefined;
    }

    root.classList.add('js-reveal');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        // Trigger a little before the element reaches the viewport, and ignore
        // the bottom edge so tall sections reveal on approach rather than late.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.06,
      }
    );

    const scan = () => {
      const targets = document.querySelectorAll(
        '[data-reveal]:not(.is-visible), [data-reveal-stagger]:not(.is-visible)'
      );
      targets.forEach((el) => {
        if (el.dataset.revealBound) return;
        el.dataset.revealBound = '1';

        // Index staggered children so CSS can offset each one
        if (el.hasAttribute('data-reveal-stagger')) {
          Array.from(el.children).forEach((child, i) => {
            child.style.setProperty('--i', i);
          });
        }

        if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
          el.classList.add('is-visible'); // already in view on load
        } else {
          observer.observe(el);
        }
      });
    };

    scan();

    /* Safety net — this is load-bearing, not belt-and-braces.

       IntersectionObserver batches its callbacks and drops elements that
       transit the viewport quickly. Worse, iOS Safari throttles or withholds
       scroll events during momentum scrolling, so a scroll listener alone is
       not enough either — a hard flick can land the reader in the middle of a
       page whose content never got the reveal class and is stuck at opacity 0.

       Both failure modes were observed in testing. So visibility is guaranteed
       three ways: the observer (nice-to-have), a scroll sweep, and a polling
       sweep that keeps running until every target has been revealed and then
       stops entirely. Polling is unattractive but it is 4/second against a
       shrinking node list, and permanently invisible content is not a
       trade worth making to avoid it. */
    let ticking = false;
    let poll = null;

    const stopWatching = () => {
      window.removeEventListener('scroll', onScroll);
      if (poll) { window.clearInterval(poll); poll = null; }
    };

    const sweep = () => {
      ticking = false;
      const remaining = document.querySelectorAll(
        '[data-reveal]:not(.is-visible), [data-reveal-stagger]:not(.is-visible)'
      );
      if (remaining.length === 0) {
        stopWatching();
        return;
      }
      remaining.forEach((el) => {
        // Reveal anything at or above the fold, plus a margin so content is
        // never caught mid-transition at the bottom edge of a fast scroll.
        if (el.getBoundingClientRect().top < window.innerHeight * 1.1) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      });
    };

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sweep);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    poll = window.setInterval(sweep, 250);

    /* Pages are lazy-loaded, so route content can mount after this effect
       runs. Watch the main landmark and bind anything that appears late. */
    const main = document.getElementById('main-content');
    const mutations = main ? new MutationObserver(() => scan()) : null;
    mutations?.observe(main, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations?.disconnect();
      stopWatching(); // clears the scroll listener and the polling interval
    };
  }, [pathname]);
};

export default useReveal;
